import { memo, useMemo, useRef, useState } from "react";
import { Calendar, ExternalLink, MoreVertical, Pencil, User } from "lucide-react";
import { Button } from "@/modern/components/ui/button";
import { Button as ButtonShadcn } from "@/modern/components/ui/button-shadcn";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/modern/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import { cn } from "@/modern/utils";
import { MarketingPage } from "@/bryntum-lib/models/MarketingPage";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { NamePromptModal } from "@/react/components/NamePromptModal";
import { MarketingPagePreview } from "./MarketingPagePreview";

const NEW_THRESHOLD_MS = 30_000;
/** Ignore card clicks briefly after dialog/menu interactions (Radix click-through). */
const CARD_CLICK_GUARD_MS = 300;

const formatDate = (d: Date | string | null | undefined) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

function pageHref(rawUrl: string) {
  if (!rawUrl) return "";
  if (rawUrl.startsWith("http://") || rawUrl.startsWith("https://")) return rawUrl;
  return `https://${rawUrl}`;
}

function pageDisplayTitle(page: MarketingPage): string {
  return page.title?.trim() || "Untitled page";
}

interface MarketingPageCardProps {
  page: MarketingPage;
  highlight?: boolean;
  isSelected?: boolean;
  onToggleSelect: (page: MarketingPage) => void;
}

function MarketingPageCardComponent({
  page,
  highlight,
  isSelected = false,
  onToggleSelect,
}: MarketingPageCardProps) {
  const { project } = useSwivuBryntumContext();
  const href = pageHref(typeof page.url === "string" ? page.url : "");
  const slug = page.slug?.trim() || "page";
  const [menuOpen, setMenuOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const skipCardClickRef = useRef(false);

  const isNew =
    page.createdAt != null &&
    Date.now() - new Date(page.createdAt).getTime() < NEW_THRESHOLD_MS;

  const creatorName = useMemo(() => {
    if (page.creator == null || !project) return null;
    const boardUser = project.boardUsersStore?.getById(page.creator) as { name?: string } | undefined;
    return boardUser?.name ?? null;
  }, [project, page.creator]);

  const guardCardClick = () => {
    skipCardClickRef.current = true;
    window.setTimeout(() => {
      skipCardClickRef.current = false;
    }, CARD_CLICK_GUARD_MS);
  };

  const openRenameModal = () => {
    guardCardClick();
    setMenuOpen(false);
    setRenameOpen(true);
  };

  const handleRenameOpenChange = (open: boolean) => {
    if (!open) guardCardClick();
    setRenameOpen(open);
  };

  const onCardClick = () => {
    if (skipCardClickRef.current) return;
    onToggleSelect(page);
  };

  const onOpenPage = (e: React.MouseEvent) => {
    e.stopPropagation();
    guardCardClick();
    if (!href || typeof window === "undefined") return;
    window.open(href, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <article
        className={cn(
          "group relative flex min-w-0 cursor-pointer flex-col overflow-hidden rounded-xl border border-border bg-card text-left shadow-sm transition-all",
          "hover:border-foreground/30 hover:shadow-md",
          "focus-within:ring-2 focus-within:ring-ring/50",
          highlight && "border-primary ring-2 ring-primary/25 shadow-md",
          isSelected && "border-primary/70 ring-2 ring-primary/20 shadow-md",
          isNew && "animate-card-highlight",
        )}
        onClick={onCardClick}
        id={`marketing-page-row-${page.id}`}
      >
        <div className="flex flex-1 flex-col gap-2 p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex w-fit max-w-full min-w-0 items-center rounded-md border border-border/60 bg-muted px-2 py-0.5 font-mono text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    <span className="truncate">{slug}</span>
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs font-mono text-[10px] uppercase">
                  {slug}
                </TooltipContent>
              </Tooltip>
              <h3 className="line-clamp-2 text-sm font-semibold text-foreground">
                {pageDisplayTitle(page)}
              </h3>
            </div>

            <div className="flex shrink-0 items-center gap-0.5">
              {href && (
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="size-8 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
                  onClick={onOpenPage}
                  aria-label="Open page in new tab"
                >
                  <ExternalLink className="size-3.5" />
                </Button>
              )}
              <DropdownMenu open={menuOpen && !renameOpen} onOpenChange={setMenuOpen}>
                <DropdownMenuTrigger asChild>
                  <ButtonShadcn
                    variant="ghost"
                    size="icon"
                    className="size-8 rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    onClick={(e) => e.stopPropagation()}
                    aria-label="Page actions"
                  >
                    <MoreVertical className="size-3.5" />
                  </ButtonShadcn>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-44" onClick={(e) => e.stopPropagation()}>
                  <DropdownMenuItem className="gap-2 text-xs" onSelect={openRenameModal}>
                    <Pencil className="size-3.5" />
                    Rename
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {href ? (
            <MarketingPagePreview href={href} title={pageDisplayTitle(page)} />
          ) : (
            <div className="min-h-[2.5rem] rounded-md border border-border/60 bg-muted/30 px-2.5 py-2">
              <div className="mb-2 flex h-1.5 items-center gap-1 rounded-full bg-muted/80 px-2">
                <span className="size-1 rounded-full bg-muted-foreground/30" />
                <span className="size-1 rounded-full bg-muted-foreground/30" />
                <span className="size-1 rounded-full bg-muted-foreground/30" />
              </div>
              <div className="space-y-1.5">
                <div className="h-2 w-[85%] rounded bg-muted/70" />
                <div className="h-2 w-[55%] rounded bg-muted/50" />
                <p className="truncate font-mono text-[10px] text-muted-foreground">URL when live</p>
              </div>
            </div>
          )}
        </div>

        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 border-t border-border bg-muted/20 px-4 py-3 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <User className="h-3 w-3 shrink-0" aria-hidden />
            <span className="truncate">{creatorName ?? "Unknown"}</span>
          </span>
          <span className="inline-flex items-center gap-1">
            <Calendar className="h-3 w-3 shrink-0" aria-hidden />
            <span>{formatDate(page.createdAt)}</span>
          </span>
          {page.includeInSite && (
            <span className="ml-auto inline-flex w-fit items-center rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
              On site
            </span>
          )}
        </div>
      </article>

      <NamePromptModal
        open={renameOpen}
        onOpenChange={handleRenameOpenChange}
        title="Rename page"
        description="Enter a new title for this marketing page."
        fieldLabel="Page title"
        placeholder="Enter page title"
        initialValue={pageDisplayTitle(page)}
        onConfirm={(title) => {
          page.title = title;
        }}
      />
    </>
  );
}

function cardPropsEqual(prev: MarketingPageCardProps, next: MarketingPageCardProps) {
  if (prev.highlight !== next.highlight || prev.isSelected !== next.isSelected) return false;
  if (prev.onToggleSelect !== next.onToggleSelect) return false;
  if (prev.page.id !== next.page.id) return false;
  if (prev.page.url !== next.page.url) return false;
  if (prev.page.title !== next.page.title) return false;
  if (prev.page.slug !== next.page.slug) return false;
  if (prev.page.includeInSite !== next.page.includeInSite) return false;
  if (prev.page.creator !== next.page.creator) return false;
  if (prev.page.createdAt !== next.page.createdAt) return false;
  return true;
}

export const MarketingPageCard = memo(MarketingPageCardComponent, cardPropsEqual);
